import { useEffect, useRef } from 'react';

// Section reveal animation hook
export const useMotionReveal = () => {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('motion-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.18,
        rootMargin: '0px 0px -10% 0px'
      }
    );

    observer.observe(element);

    return () => {
      if (element) observer.unobserve(element);
    };
  }, []);

  return ref;
};

// Cursor highlight effect hook
export const useCursorHighlight = () => {
  useEffect(() => {
    if (window.matchMedia('(hover: none)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const highlight = document.createElement('div');
    highlight.className = 'motion-cursor-highlight';
    document.body.appendChild(highlight);

    let isVisible = false;

    const handleMouseMove = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const isInteractive = target.matches('button, a, input, textarea, select, [role="button"], [tabindex]');
      
      if (isInteractive && !isVisible) {
        highlight.style.opacity = '1';
        isVisible = true;
      } else if (!isInteractive && isVisible) {
        highlight.style.opacity = '0';
        isVisible = false;
      }

      highlight.style.transform = `translate(${e.clientX - 12}px, ${e.clientY - 12}px)`;
    };

    const handleMouseLeave = () => {
      highlight.style.opacity = '0';
      isVisible = false;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
      if (document.body.contains(highlight)) {
        document.body.removeChild(highlight);
      }
    };
  }, []);
};

// Hero headline cycling hook
export const useHeadlineCycle = (phrases: string[], interval: number = 4000) => {
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!ref.current || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let currentIndex = 0;
    let isVisible = true;

    const checkVisibility = () => {
      if (!ref.current) return true;
      const rect = ref.current.getBoundingClientRect();
      return rect.top < window.innerHeight && rect.bottom > 0;
    };

    const cycle = () => {
      if (!checkVisibility()) {
        setTimeout(cycle, 1000);
        return;
      }

      if (!ref.current) return;

      ref.current.classList.add('fade-out');
      
      setTimeout(() => {
        if (!ref.current) return;
        currentIndex = (currentIndex + 1) % phrases.length;
        ref.current.textContent = phrases[currentIndex];
        ref.current.classList.remove('fade-out');
        ref.current.classList.add('fade-in');
        
        setTimeout(() => {
          if (ref.current) {
            ref.current.classList.remove('fade-in');
          }
        }, 800);
      }, 800);
    };

    const intervalId = setInterval(cycle, interval);

    return () => clearInterval(intervalId);
  }, [phrases, interval]);

  return ref;
};